<template>
  <el-dialog
    :title="$tt('按钮成图')"
    :visible.sync="innerVisible"
    width="880px"
    append-to-body
    custom-class="btn-to-image-dialog"
    @closed="$emit('closed')"
  >
    <div class="btn-to-image">
      <div class="shape-row">
        <div
          v-for="item in shapes"
          :key="item.type"
          class="shape-card"
          :class="{ active: form.shape === item.type }"
          @click="selectShape(item)"
        >
          <span class="shape-demo" :style="shapeDemoStyle(item)" />
          <span>{{ $tt(item.label) }}</span>
        </div>
      </div>
      <div class="editor-row">
        <div class="preview-pane">
          <div class="preview-label">{{ $tt("实时预览") }}</div>
          <div class="preview-stage" :class="previewBg">
            <div class="preview-btn" :style="previewStyle">{{ form.text }}</div>
          </div>
          <div class="preview-bg-switch">
            <el-radio-group v-model="previewBg" size="mini">
              <el-radio-button label="light">{{ $tt("浅底") }}</el-radio-button>
              <el-radio-button label="dark">{{ $tt("深底") }}</el-radio-button>
              <el-radio-button label="grid">{{ $tt("透明格") }}</el-radio-button>
            </el-radio-group>
          </div>
        </div>
        <div class="form-pane">
          <el-form label-width="88px" size="small">
            <el-form-item :label="$tt('按钮文字')">
              <el-input v-model="form.text" maxlength="20" show-word-limit :placeholder="$tt('如：确定')" />
            </el-form-item>
            <el-form-item :label="$tt('尺寸')">
              <div class="inline-fields">
                <span>W</span>
                <el-input-number v-model="form.width" :min="24" :max="400" :step="1" @change="onWidthChange" />
                <span>H</span>
                <el-input-number
                  v-model="form.height"
                  :min="24"
                  :max="400"
                  :step="1"
                  :disabled="form.shape === 'round'"
                  @change="onHeightChange"
                />
              </div>
            </el-form-item>
            <el-form-item :label="$tt('背景颜色')">
              <div class="inline-fields">
                <el-color-picker v-model="form.backgroundColor" show-alpha />
                <el-button size="mini" @click="form.backgroundColor = null">{{ $tt("无背景") }}</el-button>
              </div>
            </el-form-item>
            <el-form-item :label="$tt('文字颜色')">
              <el-color-picker v-model="form.color" />
            </el-form-item>
            <el-form-item :label="$tt('文字大小')">
              <el-input-number v-model="form.fontSize" :min="10" :max="48" />
            </el-form-item>
            <el-form-item :label="$tt('文字字体')">
              <el-select v-model="form.fontFamily" :placeholder="$tt('默认字体')" clearable>
                <el-option
                  v-for="opt in fontFamilyOptions"
                  :key="opt.id"
                  :label="$tt(opt.text)"
                  :value="opt.id"
                />
              </el-select>
            </el-form-item>
            <el-form-item :label="$tt('字重')">
              <el-select v-model="form.fontWeight">
                <el-option :value="400" :label="$tt('常规')" />
                <el-option :value="500" :label="$tt('中等')" />
                <el-option :value="700" :label="$tt('加粗')" />
              </el-select>
            </el-form-item>
            <el-form-item :label="$tt('圆角')" v-if="form.shape !== 'round'">
              <el-slider v-model="form.borderRadius" :min="0" :max="maxRadius" show-input />
            </el-form-item>
            <el-form-item :label="$tt('边框')">
              <div class="inline-fields">
                <el-input-number v-model="form.borderWidth" :min="0" :max="8" />
                <el-color-picker v-model="form.borderColor" />
              </div>
            </el-form-item>
            <el-form-item :label="$tt('透明度')">
              <el-slider v-model="form.opacityPct" :min="20" :max="100" />
            </el-form-item>
          </el-form>
        </div>
      </div>
    </div>
    <span slot="footer">
      <el-button @click="innerVisible = false">{{ $tt("取 消") }}</el-button>
      <el-button @click="handleDownload">{{ $tt("下载图片") }}</el-button>
      <el-button type="primary" :loading="saving" @click="handleSaveMaterial">{{ $tt("保存到私有素材库") }}</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { fontFamilyOptions } from "@/utils/attrNameData";
import {
  drawButtonToCanvas,
  canvasToPngDataUrl,
  dataUrlToBase64,
  downloadDataUrl,
  getCssFontFamily,
  isTransparentFill,
} from "./exportButtonPng";

const SHAPE_DEFAULTS = {
  square: { width: 100, height: 34, borderRadius: 0 },
  round: { width: 80, height: 80, borderRadius: 40 },
  "round-angle": { width: 100, height: 34, borderRadius: 20 },
};

export default {
  props: {
    visible: Boolean,
  },
  data() {
    return {
      innerVisible: false,
      saving: false,
      previewBg: "grid",
      fontFamilyOptions,
      shapes: [
        { type: "round", label: "圆形按钮" },
        { type: "square", label: "方形按钮" },
        { type: "round-angle", label: "圆角按钮" },
      ],
      form: {
        shape: "round-angle",
        text: "按钮",
        width: 100,
        height: 34,
        borderRadius: 20,
        backgroundColor: "#409eff",
        color: "#ffffff",
        fontSize: 14,
        fontFamily: "",
        fontWeight: 500,
        borderWidth: 0,
        borderColor: "#ffffff",
        opacityPct: 100,
      },
    };
  },
  computed: {
    maxRadius() {
      return Math.floor(Math.min(this.form.width, this.form.height) / 2);
    },
    previewStyle() {
      const radius =
        this.form.shape === "round"
          ? "50%"
          : `${this.form.borderRadius}px`;
      const family = this.form.fontFamily
        ? `"${getCssFontFamily(this.form.fontFamily)}"`
        : "Microsoft YaHei, sans-serif";
      return {
        width: `${this.form.width}px`,
        height: `${this.form.height}px`,
        lineHeight: `${this.form.height}px`,
        borderRadius: radius,
        backgroundColor: isTransparentFill(this.form.backgroundColor)
          ? "transparent"
          : this.form.backgroundColor,
        color: this.form.color,
        fontSize: `${this.form.fontSize}px`,
        fontWeight: this.form.fontWeight,
        fontFamily: family,
        opacity: this.form.opacityPct / 100,
        border:
          this.form.borderWidth > 0
            ? `${this.form.borderWidth}px solid ${this.form.borderColor}`
            : "none",
      };
    },
  },
  watch: {
    visible: {
      immediate: true,
      handler(v) {
        this.innerVisible = v;
      },
    },
    innerVisible(v) {
      this.$emit("update:visible", v);
    },
  },
  methods: {
    shapeDemoStyle(item) {
      const d = SHAPE_DEFAULTS[item.type];
      const isRound = item.type === "round";
      return {
        width: isRound ? "28px" : "36px",
        height: isRound ? "28px" : "16px",
        borderRadius: isRound ? "50%" : `${Math.min(8, d.borderRadius)}px`,
        background: this.form.shape === item.type ? "#409eff" : "#c0c4cc",
      };
    },
    selectShape(item) {
      const d = SHAPE_DEFAULTS[item.type];
      this.form.shape = item.type;
      this.form.width = d.width;
      this.form.height = d.height;
      this.form.borderRadius = d.borderRadius;
    },
    onWidthChange(val) {
      if (this.form.shape === "round") {
        this.form.height = val;
        this.form.borderRadius = Math.floor(val / 2);
      }
    },
    onHeightChange(val) {
      if (this.form.shape === "round") {
        this.form.width = val;
      }
    },
    async buildCanvas() {
      if (document.fonts && document.fonts.ready) {
        try {
          await document.fonts.ready;
        } catch (e) {
          /* ignore */
        }
      }
      return drawButtonToCanvas({
        shape: this.form.shape,
        width: this.form.width,
        height: this.form.height,
        borderRadius: this.form.borderRadius,
        backgroundColor: this.form.backgroundColor,
        color: this.form.color,
        fontSize: this.form.fontSize,
        fontFamily: this.form.fontFamily,
        fontWeight: this.form.fontWeight,
        borderWidth: this.form.borderWidth,
        borderColor: this.form.borderColor,
        opacity: this.form.opacityPct / 100,
        text: this.form.text,
      });
    },
    fileName() {
      const map = { round: "圆形", square: "方形", "round-angle": "圆角" };
      const label = this.form.text || map[this.form.shape] || "按钮";
      return `按钮成图-${label}-${Date.now()}.png`;
    },
    async handleDownload() {
      const url = canvasToPngDataUrl(await this.buildCanvas());
      downloadDataUrl(url, this.fileName());
      this.$message.success("已开始下载");
    },
    async handleSaveMaterial() {
      this.saving = true;
      try {
        const url = canvasToPngDataUrl(await this.buildCanvas());
        const res = await this.$request.uploadMaterialForm({
          base64: dataUrlToBase64(url),
          fileName: this.fileName(),
          fileType: "picture",
        });
        if (res && res.status === "success") {
          this.$message.success("已保存到私有素材库");
        } else {
          this.$message.error((res && res.msg) || "保存失败");
        }
      } catch (e) {
        this.$message.error("保存失败，请检查网络");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss">
.btn-to-image-dialog {
  .el-dialog__body {
    padding: 12px 20px;
  }
}
</style>
<style lang="scss" scoped>
.btn-to-image {
  text-align: left;
}
.shape-row {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}
.shape-card {
  flex: 1;
  height: 72px;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  font-size: 13px;
  color: #606266;
  &.active {
    border-color: #409eff;
    color: #409eff;
    background: #ecf5ff;
  }
}
.editor-row {
  display: flex;
  gap: 16px;
}
.preview-pane {
  width: 320px;
  flex-shrink: 0;
}
.preview-label {
  font-size: 13px;
  color: #909399;
  margin-bottom: 8px;
}
.preview-stage {
  height: 240px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  &.light {
    background: #f5f7fa;
  }
  &.dark {
    background: #303133;
  }
  &.grid {
    background-color: #fff;
    background-image: linear-gradient(45deg, #eee 25%, transparent 25%),
      linear-gradient(-45deg, #eee 25%, transparent 25%),
      linear-gradient(45deg, transparent 75%, #eee 75%),
      linear-gradient(-45deg, transparent 75%, #eee 75%);
    background-size: 16px 16px;
    background-position: 0 0, 0 8px, 8px -8px, -8px 0;
  }
}
.preview-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 0 8px;
  user-select: none;
}
.preview-bg-switch {
  margin-top: 10px;
  text-align: center;
}
.form-pane {
  flex: 1;
  max-height: 420px;
  overflow: auto;
}
.inline-fields {
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>
